import React, { useMemo } from 'react';
import { BasicContainer, BodyTextSmall, ButtonAsWrapper, FlexCol, FlexRow } from '@minecraft.atoms';
import { TabbedComponentProps, validateTabsProp } from './utils';

/**
 * Tabbed Section takes a list of objects that describe each tab, as well as the active tab and a callback when a tab is clicked
 *
 * the tab buttons & tab panels are a11y friendly and provide a standard UI on mobile & desktop for the CN design system
 */
export const TabbedSection = <TabKey extends string>({
  activeTab,
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  onTabChange,
  tabs,
}: TabbedComponentProps<TabKey>) => {
  const tabsToUse = useMemo(() => validateTabsProp({ tabs, componentName: 'TabbedSection' }), [tabs]);

  return (
    <FlexCol data-qa-id={dataQaId} data-testid={dataTestId}>
      <FlexRow
        maxHeight={3}
        role="tablist"
        className="cn_overflow-x-auto"
        alignItems="flex-end"
        wrap="nowrap"
        gap="4"
        w="full"
      >
        {tabsToUse.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <ButtonAsWrapper
              aria-controls={`${tab.key}-tabpanel`}
              aria-label={tab.tabTitle}
              aria-selected={isActive}
              data-qa-id={`${tab.key}-tab`}
              disabled={isActive}
              id={`${tab.key}-tabbutton`}
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              role="tab"
            >
              <BasicContainer
                key={tab.key}
                borderPlacement="bottom"
                borderColor={isActive ? 'emphasis' : 'transparent'}
                borderSize={0.09}
                className="cn_atom_py-2"
              >
                <BodyTextSmall
                  noWrap
                  fontWeight={isActive ? 'semibold' : 'medium'}
                  color={isActive ? 'default' : 'primary'}
                >
                  {tab.tabTitle}
                </BodyTextSmall>
              </BasicContainer>
            </ButtonAsWrapper>
          );
        })}
      </FlexRow>
      {tabsToUse.map((tab) => (
        <BasicContainer
          aria-labelledby={`${tab.key}-tabbutton`}
          className="cn_atom_py-2"
          hidden={tab.key !== activeTab}
          id={`${tab.key}-tabpanel`}
          key={tab.key}
          role="tabpanel"
          w="full"
        >
          {tab.Component}
        </BasicContainer>
      ))}
    </FlexCol>
  );
};
